// ─────────────────────────────────────────────────────────────────────────────
// SwipeableOffcanvas — drop-in replacement do <Offcanvas> do react-bootstrap com
// gesto de "swipe to go back" (edge swipe) embutido.
//
// Uso (idêntico ao Offcanvas):
//   <SwipeableOffcanvas show={show} onHide={onHide} placement="end">
//     <Offcanvas.Header closeButton>...</Offcanvas.Header>
//     <Offcanvas.Body>...</Offcanvas.Body>
//   </SwipeableOffcanvas>
//
// Ao arrastar a partir da borda da tela, chama onHide. Em overlays empilhados,
// só o do topo fecha (ver utils/edgeSwipeManager). Para desligar o gesto numa
// instância específica, passe swipeToClose={false}.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useRef } from "react";
import s from './SwipeableOffcanvas.module.scss';
import { Offcanvas } from "react-bootstrap";
import { pushEdgeSwipe, popEdgeSwipe } from "../../utils/edgeSwipeManager";
import { registrarOffcanvas, removerOffcanvas } from "../../utils/offcanvasStack";

const RedesignContext = createContext(false);

export default function SwipeableOffcanvas({
  show,
  onHide,
  swipeToClose = true,
  swipeSide = "both",
  children,
  className,
  ...rest
}) {
  const redesign = useContext(RedesignContext);
  // Mantém a referência mais recente do onHide sem re-registrar na pilha.
  const onHideRef = useRef(onHide);
  onHideRef.current = onHide;

  useEffect(() => {
    if (!show || !swipeToClose) return undefined;
    const id = pushEdgeSwipe(() => onHideRef.current && onHideRef.current(), swipeSide);
    return () => popEdgeSwipe(id);
  }, [show, swipeToClose, swipeSide]);

  // Pilha de offcanvas abertos (independe do gesto): é o que o botão voltar
  // do Android consulta para fechar só o do topo. Ver utils/offcanvasStack.
  useEffect(() => {
    if (!show) return undefined;
    const id = registrarOffcanvas(() => onHideRef.current && onHideRef.current());
    return () => removerOffcanvas(id);
  }, [show]);

  return (
    // ── POR QUE O FOCO DO REACT-BOOTSTRAP É DESLIGADO AQUI ───────────────────
    //
    // `enforceFocus` e `restoreFocus` são afordâncias de DIÁLOGO DE DESKTOP:
    // prender o Tab dentro da caixa e devolver o foco ao botão que a abriu.
    // Num app de toque, com offcanvas de tela cheia (`width: 100% !important`),
    // nenhuma das duas faz nada de útil — e as duas mexem no foco sozinhas, num
    // instante que não é o do dedo da pessoa.
    //
    // `enforceFocus` instala um listener de `focus` no documento POR OFFCANVAS
    // ABERTO (@restart/ui/Modal, `handleShow`). Quem está no topo da pilha do
    // ModalManager e recebe um foco que não está dentro do seu próprio painel
    // responde com `dialog.focus()` — puxando o foco de volta. Enquanto um
    // offcanvas empilhado está SAINDO ele continua sendo o do topo (só sai da
    // pilha quando a transição de saída termina), então um toque num campo do
    // offcanvas de baixo, nesse intervalo, era desfeito ~0 ms depois: o campo
    // recebia foco e perdia, e no iOS o teclado simplesmente não abria. Era o
    // "clico no campo e não acontece nada" do registro de treino, logo depois
    // de escolher a atividade — e passava sozinho quando qualquer outra coisa
    // fazia a pilha andar.
    //
    // `restoreFocus` é o mesmo problema pelo outro lado: ao fechar, ele devolve
    // o foco ao elemento que estava ativo na abertura — um `focus()` atrasado,
    // disparado quando a transição acaba, sobre um nó que a tela de baixo pode
    // até já ter trocado (o passo 2 do check-in remonta o formulário inteiro
    // com `key={formStep}`). Já tinha custado um defeito visível antes: o
    // refoco rolava o scroller do app e "o scroll do pai não voltava", que é o
    // que o `restoreFocusOptions={{ preventScroll: true }}` remendava. Sem
    // restaurar foco nenhum, não há o que remendar.
    //
    // O que fica: `autoFocus`, que aponta o foco para o painel ao abrir. Esse é
    // útil — tira o foco do que ficou atrás.
    <Offcanvas
      show={show}
      onHide={onHide}
      enforceFocus={false}
      restoreFocus={false}
      {...rest}
      className={[className, redesign && s.redesign].filter(Boolean).join(' ')}
    >
      {children}
    </Offcanvas>
  );
}

// Conveniência: permite usar <SwipeableOffcanvas.Body> etc. se desejado.
SwipeableOffcanvas.Body = Offcanvas.Body;
SwipeableOffcanvas.Header = Offcanvas.Header;
SwipeableOffcanvas.Title = Offcanvas.Title;
SwipeableOffcanvas.Redesign = ({ children }) => <RedesignContext.Provider value>{children}</RedesignContext.Provider>;
